/**
 * Cycle Complete Page
 * 
 * Displays a completion summary screen after an operator signs off the final store in a cycle.
 * Shows cycle summary and allows navigation to the gallery.
 */

import { useEffect } from 'react';
import { useParams, useLocation } from 'wouter';
import { useAuthUser } from '@/hooks/useAuthUser';
import { useCycleSummary } from '@/hooks/useCycleSummary';
import { MobileScaffold } from '@/components/mobile/MobileScaffold';
import { CycleLandingCard } from '@/components/mobile/CycleLandingCard';
import { Loader2 } from 'lucide-react';

export default function CycleCompletePage() {
  const [, setLocation] = useLocation();
  const params = useParams<{ cycleId: string }>();
  const cycleId = params?.cycleId ? parseInt(params.cycleId) : undefined;
  const { user } = useAuthUser();
  const operatorId = user?.id ?? (user as any)?.userId ?? (user as any)?.localId;

  const { data, isLoading, error } = useCycleSummary(operatorId, cycleId);
  const completionData = data as any;

  useEffect(() => {
    if (cycleId && data?.status === 'signed_off') {
      localStorage.setItem(`cycle_${cycleId}_complete_shown`, 'true');
    }
  }, [cycleId, data?.status]);

  if (isLoading) {
    return (
      <MobileScaffold title="Cycle Complete">
        <div className="flex items-center justify-center h-full">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </MobileScaffold>
    );
  }

  if (error || !data) {
    return (
      <MobileScaffold title="Cycle Complete">
        <div className="p-4 text-center text-gray-600">
          Failed to load cycle summary.
        </div>
      </MobileScaffold>
    );
  }

  // ✅ ENFORCEMENT: Only show completion page if cycle is truly signed off
  if (completionData.status !== 'signed_off') {
    console.warn('[CYCLE_COMPLETE_PAGE] Cycle is not signed off yet', {
      status: completionData.status,
      signedStores: completionData.signedStores,
      totalStores: completionData.totalStores,
      awaitingSignatureCount: completionData.awaitingSignatureCount,
    });
    
    return (
      <MobileScaffold title="Cycle In Progress">
        <div className="p-4 space-y-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
            <h2 className="font-semibold text-yellow-900 mb-2">Cycle Still In Progress</h2>
            <p className="text-yellow-800 text-sm mb-4">
              {completionData.awaitingSignatureCount > 0
                ? `${completionData.awaitingSignatureCount} store(s) still awaiting manager signature.`
                : 'Not all stores are complete yet.'}
            </p>
            <p className="text-yellow-700 text-xs mb-4">
              Progress: {completionData.signedStores}/{completionData.totalStores} stores signed off
            </p>
            {completionData.storesAwaitingSignature && completionData.storesAwaitingSignature.length > 0 && (
              <div className="text-left bg-white rounded p-2 text-xs mb-4 max-h-32 overflow-y-auto">
                <p className="font-semibold mb-2">Awaiting Signature:</p>
                <ul className="space-y-1">
                  {completionData.storesAwaitingSignature.map((store: any) => (
                    <li key={store.storeId} className="text-yellow-800">
                      • {store.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <button
              onClick={() => setLocation('/route-overview')}
              className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded text-sm"
            >
              Back to Route
            </button>
          </div>
        </div>
      </MobileScaffold>
    );
  }

  return (
    <MobileScaffold title="Cycle Complete">
      <CycleLandingCard
        mode="complete"
        cycleName={completionData.cycleName}
        period={completionData.period}
        retailerName={completionData.retailerName}
        totalStores={completionData.totalStores}
        totalCampaigns={completionData.totalCampaigns}
        totalTasks={completionData.totalTasks}
        campaigns={completionData.campaigns}
        completedAt={completionData.completedAt}
        signedOffBy={completionData.signedOffBy}
        onAction={() => setLocation(`/image-gallery?cycleId=${cycleId}`)}
        onBack={() => setLocation('/route-overview')}
      />
    </MobileScaffold>
  );
}
